import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import StackedBarChart from '../components/charts/StackedBarChart'
import LineChart from '../components/charts/LineChart'
import {
  TrophyIcon,
  XCircleIcon,
  ChartPieIcon,
  CurrencyDollarIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatPercent,
  formatDate,
  getCountryName
} from '../utils/formatters'

const STAGES = ['MQL', 'SQL', 'Opportunity', 'Proposal', 'Contract Sent']

export default function MarketingSalesDeals() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedStage, setSelectedStage] = useState(null)
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const start = startMonth || months[Math.max(0, months.length - 12)]
    const end = endMonth || months[months.length - 1]
    const filteredMonths = months.filter(m => m >= start && m <= end)

    // Filter deals
    let deals = data.deals.filter(d => {
      const mqlMonth = d.mql_date?.substring(0, 7)
      return mqlMonth && mqlMonth >= start && mqlMonth <= end
    })

    if (selectedCountry) {
      deals = deals.filter(d => d.country === selectedCountry)
    }

    if (selectedStage) {
      deals = deals.filter(d => d.current_stage === selectedStage)
    }

    // Won vs Lost by month
    const wonByMonth = filteredMonths.map(month =>
      deals.filter(d => d.closed_won_date?.substring(0, 7) === month).length
    )
    const lostByMonth = filteredMonths.map(month =>
      deals.filter(d => d.closed_lost_date?.substring(0, 7) === month).length
    )

    // Pipeline development - deals in each stage by month
    const pipelineByMonth = STAGES.map(stage => ({
      label: stage,
      data: filteredMonths.map(month => {
        // Count deals that were in this stage during this month
        return deals.filter(d => {
          const stageDate = stage === 'MQL' ? d.mql_date :
                           stage === 'SQL' ? d.sql_date :
                           stage === 'Opportunity' ? d.opportunity_date :
                           stage === 'Proposal' ? d.proposal_date :
                           d.contract_sent_date
          return stageDate?.substring(0, 7) === month
        }).length
      })
    }))

    // Pipeline value by month
    const pipelineValueByMonth = STAGES.map(stage => ({
      label: stage,
      data: filteredMonths.map(month => {
        const stageDeals = deals.filter(d => {
          const stageDate = stage === 'MQL' ? d.mql_date :
                           stage === 'SQL' ? d.sql_date :
                           stage === 'Opportunity' ? d.opportunity_date :
                           stage === 'Proposal' ? d.proposal_date :
                           d.contract_sent_date
          return stageDate?.substring(0, 7) === month
        })
        return stageDeals.reduce((sum, d) => sum + (d.weighted_deal_value || 0), 0)
      })
    }))

    // Summary stats
    const closedWon = deals.filter(d => d.closed_won_date)
    const closedLost = deals.filter(d => d.closed_lost_date)
    const openDeals = deals.filter(d => !d.closed_won_date && !d.closed_lost_date)
    const totalPipelineValue = openDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0)
    const winRate = (closedWon.length + closedLost.length) > 0
      ? closedWon.length / (closedWon.length + closedLost.length)
      : 0

    // Table data
    const tableData = deals.map(d => ({
      ...d,
      id: d.deal_id
    }))

    return {
      months: filteredMonths,
      wonByMonth,
      lostByMonth,
      pipelineByMonth,
      pipelineValueByMonth,
      closedWon,
      closedLost,
      openDeals,
      totalPipelineValue,
      winRate,
      tableData
    }
  }, [data, computed, selectedCountry, selectedStage, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    wonByMonth,
    lostByMonth,
    pipelineByMonth,
    pipelineValueByMonth,
    closedWon,
    closedLost,
    openDeals,
    totalPipelineValue,
    winRate,
    tableData
  } = filteredData

  const allStages = ['MQL', 'SQL', 'Opportunity', 'Proposal', 'Contract Sent', 'Closed Won', 'Closed Lost']

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <FilterDropdown
            label="Country"
            value={selectedCountry}
            options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
            onChange={setSelectedCountry}
            placeholder="All Countries"
          />
          <FilterDropdown
            label="Stage"
            value={selectedStage}
            options={allStages}
            onChange={setSelectedStage}
            placeholder="All Stages"
          />
        </div>
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard
          title="Deals Won"
          value={closedWon.length.toLocaleString()}
          icon={TrophyIcon}
          colorScheme="success"
        />
        <KPICard
          title="Deals Lost"
          value={closedLost.length.toLocaleString()}
          icon={XCircleIcon}
          colorScheme="danger"
        />
        <KPICard
          title="Win Rate"
          value={formatPercent(winRate)}
          icon={ChartPieIcon}
          colorScheme={winRate >= 0.3 ? 'success' : 'warning'}
        />
        <KPICard
          title="Pipeline Value"
          value={formatCurrency(totalPipelineValue, { compact: true })}
          subtitle={`${openDeals.length} open deals`}
          icon={CurrencyDollarIcon}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Closed Won vs Lost" subtitle="Monthly comparison" height="h-64">
          <StackedBarChart
            labels={months}
            datasets={[
              { label: 'Closed Won', data: wonByMonth, color: '#22c55e' },
              { label: 'Closed Lost', data: lostByMonth, color: '#ef4444' }
            ]}
            yAxisPrefix=""
          />
        </ChartCard>

        <ChartCard title="Pipeline Development" subtitle="Weighted deal value by stage" height="h-64">
          <StackedBarChart
            labels={months}
            datasets={pipelineValueByMonth}
          />
        </ChartCard>
      </div>

      <ChartCard title="Pipeline Development (Totals)" subtitle="Deal count by stage" height="h-64">
        <StackedBarChart
          labels={months}
          datasets={pipelineByMonth}
          yAxisPrefix=""
        />
      </ChartCard>

      {/* Deals Table */}
      <ChartCard title="Deals Overview" height="h-auto">
        <DataTable
          columns={[
            { key: 'deal_name', header: 'Deal Name' },
            { key: 'hubspot_link', header: 'HubSpot', render: v => v ? (
              <a
                href={v}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-700 dark:text-brand-400"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </a>
            ) : '-' },
            { key: 'mql_date', header: 'MQL Date', render: v => formatDate(v) },
            { key: 'sql_date', header: 'SQL Date', render: v => formatDate(v) },
            { key: 'opportunity_date', header: 'Opp Date', render: v => formatDate(v) },
            { key: 'closed_won_date', header: 'Won Date', render: v => formatDate(v) },
            { key: 'closed_lost_date', header: 'Lost Date', render: v => formatDate(v) },
            { key: 'deal_value', header: 'Value', render: v => formatCurrency(v) },
            { key: 'weighted_deal_value', header: 'Weighted', render: v => formatCurrency(v) },
            { key: 'current_stage', header: 'Stage', render: v => (
              <span className={`badge ${
                v === 'Closed Won' ? 'badge-success' :
                v === 'Closed Lost' ? 'badge-danger' :
                'badge-info'
              }`}>
                {v}
              </span>
            )}
          ]}
          data={tableData}
          pageSize={10}
          searchable={true}
          searchPlaceholder="Search deals..."
        />
      </ChartCard>
    </div>
  )
}
